import React, { PureComponent } from 'react'
import throttle from 'lodash/throttle'
import PropTypes from 'prop-types'
import { callApi, ENDPOINTS } from 'utils/api/callApi'
import { log } from 'utils/errorLogger'

const defaults = {
  fmcsaState: '',
  shipmentsCount: 0,
  isTrusted: false,
  bounceRate: 0,
  onTimePickup: '',
  newStatus: '',
  mcNumber: '',
  dotNumber: '',
  stateName: '',
}

// This HOC Loads Carrier Application and FMCSA data
// The wrapped inner class is returned at the end of file
export const CarrierDataLoader = WrappedComponent => {
  class CarrierData extends PureComponent {
    constructor(props) {
      super(props)
      this.state = { ...defaults }
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps({ id: prevId }) {
      const { id } = this.props
      if (prevId !== id) {
        this.setState({ ...defaults })
      }
    }

    loadCarrierData = throttle(
      () => {
        const { dotNumber, fmcsaState } = this.state
        const { loadImmediately } = this.props
        if (loadImmediately && (!fmcsaState || !dotNumber)) {
          this.loadCarrierApplication()
          this.loadFmcsa()
        }
      },
      1000,
      { leading: true, trailing: false }
    )

    loadFmcsa = async () => {
      try {
        const { id } = this.props
        const fmcsaRequestData = {
          endpoint: ENDPOINTS.carriers,
          id,
          group: 'fmcsa_carrier_information',
        }
        const data = await callApi(fmcsaRequestData)
        const {
          fmcsaCarrierInformation: {
            carrierApplication: { state },
            rating,
          },
        } = data
        this.setState({ fmcsaState: rating, newStatus: state })
      } catch (e) {
        log('carrier_data', e) // 'Unable to load FMCSA data in CarrierData'
      }
    }

    loadCarrierApplication = async () => {
      try {
        const { id } = this.props
        const carrierRequestData = { id, endpoint: ENDPOINTS.carriers }
        const data = await callApi(carrierRequestData)
        const { carrierApplication } = data
        const {
          state,
          mcNumber,
          dotNumber,
          isTrusted,
          legalName,
          carrierScore: {
            notBouncePct: bounceRate,
            otp30: onTimePickup,
            totalLoadsInScore: shipmentsCount,
          } = { notBouncePct: '', otp30: '', totalLoadsInScore: 0 },
          carrierManagers = [],
        } = carrierApplication
        const primaryManager = carrierManagers.filter(
          ({ relationshipType }) => relationshipType === 'owner'
        )[0]
        this.setState({
          stateName: state,
          isTrusted,
          mcNumber,
          dotNumber,
          shipmentsCount,
          onTimePickup,
          bounceRate: bounceRate ? 100 - bounceRate : 0,
          primaryManager,
          legalName,
        })
      } catch (e) {
        log('Unable to load carrier data in CarrierData', e)
      }
    }

    render() {
      this.loadCarrierData()
      const { id, loadImmediately } = this.props
      const {
        bounceRate,
        dotNumber,
        fmcsaState,
        isTrusted,
        legalName,
        mcNumber,
        newStatus,
        onTimePickup,
        primaryManager,
        shipmentsCount,
        stateName,
      } = this.state
      return (
        <WrappedComponent
          fmcsaState={fmcsaState}
          shipmentsCount={shipmentsCount}
          isTrusted={isTrusted}
          bounceRate={bounceRate}
          onTimePickup={onTimePickup}
          newStatus={newStatus}
          mcNumber={mcNumber}
          dotNumber={dotNumber}
          stateName={stateName}
          legalName={legalName}
          primaryManager={primaryManager}
          id={id}
          loadImmediately={loadImmediately}
        />
      )
    }
  }

  CarrierData.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    loadImmediately: PropTypes.bool,
  }

  CarrierData.defaultProps = {
    loadImmediately: false,
  }

  return CarrierData
}
